import { useMemo } from 'react';
import { useInteractiveModeSession } from 'copilot-toolkit/hooks/useInteractiveModeSession';
import unescapedText from 'I18n/utils/unescapedText';
import { useFilterActionsModule } from './useFilterActionsModule';
import { useSearchModule } from './useSearchModule';
const LLM_INSTRUCTIONS = `You are a helpful assistant for users browsing HubSpot's Marketplace storefront. You help users discover and filter apps and Breeze Agents.

## Available Filters

1. **Product Type** — Filter by product type: Apps or Breeze Agents.
2. **Compatible with my plan** — Toggle to only show products compatible with the user's current HubSpot plan (in-app only).
3. **Works with Breeze** — Toggle to only show products that integrate with Breeze.
4. **Installed** — Filter by installation status: All, Installed, or Not installed (in-app only).
5. **Price** — Filter apps by pricing plan (e.g., Free, Freemium, Paid).
6. **HubSpot features** — Filter apps by the HubSpot features they integrate with.
7. **HubSpot Certified** — Toggle to only show HubSpot Certified apps.
8. **Data flow direction** — Filter apps by sync direction: All, Sync to HubSpot, Sync from HubSpot, or Two-way sync.
9. **App language** — Filter apps by supported language.
10. **Category** — Filter apps by category (e.g., Marketing, Sales, Analytics).
11. **Built by** — Filter apps by builder: All, HubSpot, or Partners.
12. **Agent team** — Filter Breeze Agents by team: Marketing, Sales, or Service.

## Search

Users can also search by keyword. You can help refine or update their search query.

## Guidelines

- **Prefer filters over search.** When the user's request maps to one or more available filter values, always apply filters rather than using search. For example, "sales enablement apps" should apply the Category filter, not a search query.
- When users ask about apps specifically, set the Product Type filter to Apps.
- When users ask about agents or AI assistants, set the Product Type filter to Breeze Agents.
- If the user mentions pricing (e.g., "free apps"), use the Price filter.
- If the user mentions a specific category or feature, use the Category or HubSpot features filters.
- **Do not combine search with filters.** Use either filters or search to narrow results, not both at the same time. Combining them often over-constrains results and returns nothing. If you apply filters, do not also set a search query. If the user's request doesn't map to any filter values, use search alone with a short query (2-3 keywords).
- Only describe actions you have actually performed. Do not say "based on the filters just applied" unless you have actually called applyFilter.
- Suggest relevant follow-up refinements based on what filters have been applied.
- If the user's request is ambiguous, ask for clarification about what they're looking for.
- IMPORTANT: You MUST only use exact values from the possibleValues provided for each filter parameter. Do not invent, abbreviate, or guess filter values. If there is no matching filter value for the user's request, use the search functionality instead or suggest the closest available filter options.`;
export const useStorefrontFiltersInteractiveMode = ({
  enabled = false
}) => {
  const filterActions = useFilterActionsModule();
  const searchActions = useSearchModule();
  const interactiveModeActions = useMemo(() => {
    if (!enabled) {
      return [];
    }
    return [...filterActions, ...searchActions];
  }, [enabled, filterActions, searchActions]);
  const starterActions = useMemo(() => ({
    actions: enabled ? [{
      buttonText: unescapedText('marketplaceStorefront.interactiveMode.starterActions.findApps'),
      prompt: unescapedText('marketplaceStorefront.interactiveMode.starterPrompts.findApps')
    }, {
      buttonText: unescapedText('marketplaceStorefront.interactiveMode.starterActions.browseAgents'),
      prompt: unescapedText('marketplaceStorefront.interactiveMode.starterPrompts.browseAgents')
    }, {
      buttonText: unescapedText('marketplaceStorefront.interactiveMode.starterActions.freeIntegrations'),
      prompt: unescapedText('marketplaceStorefront.interactiveMode.starterPrompts.freeIntegrations')
    }] : []
  }), [enabled]);
  return useInteractiveModeSession({
    sessionId: 'marketplace-storefront-filters',
    appName: 'marketplace-storefront-filters',
    interactiveModeActions,
    starterActions,
    additionalLLMInstructions: enabled ? LLM_INSTRUCTIONS : '',
    additionalToolSets: enabled ? ['com.hubspot.marketplace.ai.tools.rpc.MarketplaceAiTools'] : []
  });
};